import { vnTemplate } from '../../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/vnTemplate';
import { listPagePagination_template as pagination_template } from '~/components/templates/listPagePagination_template';
import { resultsInfo_template } from '../../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/resultsInfo_template';
import { listPageSelect_template } from '~/components/templates/listPageSelect_template';
import { renderPartnersTable } from '~/components/recruiting/partners-table';
import { html, LpButton, LpFilterBar, LpFilterCell, LpPageHeader, LpSearchInput, raw } from '~/design-system';
import { getEntityActiveStatusOption } from '~/utils/entityActiveStatus';
const ACTIVE_STATUS_ID = 1;
const ACTIVE_STATUS_VALUE = String(ACTIVE_STATUS_ID);
export class adminPartners_template extends vnTemplate {
    /**
     * Return the template key used by the Valnet template framework.
     *
     * @return string
     */
    getTemplateName() {
        return 'adminPartners-template';
    }
    /**
     * Return the CSS classes applied to the template root element.
     *
     * @return Array<string>
     */
    getMainElementClasses() {
        return ['w-content'];
    }
    /**
     * Initialize nested template partials used by this template.
     *
     * @return void
     */
    initPartial() {
        const vm = this.getTemplateViewModel();
        this.pagination = new pagination_template('adminPartnersPagination', vm, this.caller);
        this.pagination.getTemplateViewModel().itemPerPage = 20;
        this.resultsInfo = new resultsInfo_template('adminPartnersResultInfo', vm, this.caller);
        this.statusFilter = new listPageSelect_template('adminPartnersStatusFilter', vm, this.caller);
        this.statusFilter.setLabel('Status');
        this.statusFilter.setShowLabelPrefix(true);
        this.statusFilter.setIcon('info');
        this.statusFilter.setOptions([
            getEntityActiveStatusOption({ id: ACTIVE_STATUS_VALUE, name: 'Active' }),
        ]);
        this.statusFilter.bindValue(vm.status);
        this.channelFilter = new listPageSelect_template('adminPartnersChannelFilter', vm, this.caller);
        this.channelFilter.setLabel('Channel');
        this.channelFilter.setShowLabelPrefix(true);
        this.channelFilter.setIcon('grid');
        this.channelFilter.bindValue(vm.channelId);
    }
    /**
     * Build the Knockout view model used by this template.
     *
     * @return any
     */
    buildViewModel() {
        const self = this;
        return {
            partners: ko.observableArray([]),
            status: ko.observable(ACTIVE_STATUS_VALUE),
            channelId: ko.observable(''),
            internalName: ko.observable(''),
            orderBy: ko.observable('displayOrder'),
            orderByDirection: ko.observable('ASC'),
            orderByAndDirection: ko.observable('displayOrder_ASC'),
            onClickCreate: function () { self.caller.createPartner(); },
            onClickEdit: function (row) { self.caller.editPartner(row.id); },
            onFilterChange: function () { self.caller.filterOnChange(); },
            onClickReorder: function (value, data, event) { self.onClickReorder(value, data, event); },
        };
    }
    /**
     * Return the HTML markup for this template.
     *
     * @return string
     */
    getTemplateHtml() {
        return `
            ${LpPageHeader({
            title: 'Partners',
            description: 'Manage partner offers and delivery settings.',
            icon: 'users',
            controls: LpFilterBar({
                className: 'partners-control-bar',
                controls: html `
                        ${LpFilterCell({
                    children: raw(this.statusFilter.getHtmlBuild()),
                })}

                        ${LpFilterCell({
                    children: raw(this.channelFilter.getHtmlBuild()),
                })}

                        ${LpFilterCell({
                    search: true,
                    children: LpSearchInput({
                        placeholder: 'Partner internal name',
                        attributes: {
                            'data-bind': 'value: internalName, event: { change: onFilterChange }',
                        },
                    }),
                })}
                    `,
                actions: LpButton({
                    label: 'Create New Partner',
                    prefixIcon: 'plus',
                    size: 'base',
                    variant: 'primary',
                    attributes: {
                        'data-bind': 'click: onClickCreate',
                    },
                }),
            }),
        })}

            <div class="list-page-module partners-module">
                <div class="data-module__body list-page-module__body" id="list-results">
                    ${renderPartnersTable({ getSortAttributes: (column) => this.getSortAttributes(column) })}
                </div>
                <div class="data-module__footer list-page-module__footer">
                    <div class="list-page-module__toolbar">
                        <div class="list-page-module__meta">
                            ${this.resultsInfo.getHtmlBuild()}
                        </div>
                        <div class="list-page-module__pagination">
                            ${this.pagination.getHtmlBuild()}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
    getSortAttributes(column) {
        return {
            'data-bind': `click: onClickReorder.bind($data, '${column}'), css: { 'is-desc': orderByAndDirection()=='${column}_DESC', 'is-asc': orderByAndDirection()=='${column}_ASC' }`,
        };
    }
    /**
     * Build the request payload from the current view model state.
     *
     * @return object
     */
    getViewModelForRequest() {
        const vm = this.getTemplateViewModel();
        return {
            statuses: [Number(this.getSelectedStatusOrDefault())],
            channelId: vm.channelId(),
            internalName: vm.internalName(),
            sortBy: vm.orderBy(),
            sortDirection: vm.orderByDirection(),
            pagination: this.getPaginationViewModel(),
        };
    }
    /**
     * Return the pagination partial view model.
     *
     * @return any
     */
    getPaginationViewModel() {
        return this.pagination ? this.pagination.getTemplateViewModel() : null;
    }
    /**
     * Apply reference data returned by the API to the view model.
     *
     * @param references any
     * @return void
     */
    setReferences(references) {
        const statuses = references.statuses && references.statuses.length ? references.statuses : [{ id: ACTIVE_STATUS_ID, name: 'Active' }];
        this.statusFilter.setOptions(statuses.map((status) => getEntityActiveStatusOption({
            id: status.id,
            name: status.name,
        })));
        this.channelFilter.setOptions([
            { id: '', name: 'All channels' },
            ...(references.channels || []).map((channel) => ({
                id: String(channel.id),
                name: channel.name,
            })),
        ]);
        this.setDefaultStatus();
    }
    /**
     * Set default status on the view model.
     *
     * @return void
     */
    setDefaultStatus() {
        const vm = this.getTemplateViewModel();
        vm.status(this.getSelectedStatusOrDefault());
    }
    /**
     * Apply partner references to the view model.
     *
     * @param partners Array<any>
     * @return void
     */
    setPartners(partners) {
        this.getTemplateViewModel().partners((partners || []).map((partner) => ({
            ...partner,
            partnerStatusLabel: this.getStatusLabel(partner.partnerStatus),
        })));
    }
    /**
     * Update the pagination partial with the result count.
     *
     * @param totalCount any
     * @return void
     */
    updatePagination(totalCount) {
        if (this.pagination) {
            this.pagination.setNumberItemToPaginate(totalCount);
        }
    }
    /**
     * Update the result-info partial with list metadata.
     *
     * @param totalResults number
     * @param totalPages number
     * @param execTime number
     * @return void
     */
    setResultInfo(totalResults, totalPages, execTime) {
        this.resultsInfo.setNbResults(totalResults);
        this.resultsInfo.setNbPages(totalPages);
        this.resultsInfo.setExecutionTime(execTime);
    }
    /**
     * Reset the list pagination to the first page.
     *
     * @return void
     */
    resetPageNumber() {
        const paginationVm = this.getPaginationViewModel();
        if (paginationVm && typeof paginationVm.pageNumber === 'function') {
            paginationVm.pageNumber(1);
        }
    }
    /**
     * Set view model from request on the view model.
     *
     * @param params Array<any>
     * @return void
     */
    setViewModelFromRequest(params) {
        const vm = this.getTemplateViewModel();
        if (typeof params['pagination'] !== 'undefined' && params['pagination'] && vm.pagination && typeof vm.pagination.pageNumber === 'function') {
            vm.pagination.pageNumber(Math.max(1, Number(params['pagination'].pageNumber || 1)));
        }
        if (typeof params['statuses'] !== 'undefined' && params['statuses'] && params['statuses'].length) {
            vm.status(String(params['statuses'][0]));
        }
        else if (typeof params['status'] !== 'undefined' && params['status'] !== '') {
            vm.status(String(params['status']));
        }
        else {
            vm.status(ACTIVE_STATUS_VALUE);
        }
        if (typeof params['channelId'] !== 'undefined') {
            vm.channelId(params['channelId']);
        }
        if (typeof params['internalName'] !== 'undefined') {
            vm.internalName(params['internalName']);
        }
        if (typeof params['sortBy'] !== 'undefined') {
            vm.orderBy(params['sortBy']);
        }
        if (typeof params['sortDirection'] !== 'undefined') {
            const direction = String(params['sortDirection']).toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
            vm.orderByDirection(direction);
            vm.orderByAndDirection(vm.orderBy() + '_' + direction);
        }
    }
    /**
     * Handle on click reorder.
     *
     * @param value any
     * @param data any
     * @param event any
     * @return void
     */
    onClickReorder(value, data, event) {
        const vm = this.getTemplateViewModel();
        vm.orderBy(value);
        if (vm.orderByAndDirection() && vm.orderByAndDirection().includes(value)) {
            vm.orderByDirection(vm.orderByDirection() === 'DESC' ? 'ASC' : 'DESC');
        }
        else {
            vm.orderByDirection('DESC');
        }
        vm.orderByAndDirection(value + '_' + vm.orderByDirection());
        this.caller.listReorder();
    }
    /**
     * Return status label.
     *
     * @param status any
     * @return string
     */
    getStatusLabel(status) {
        switch (Number(status)) {
            case 0:
                return 'Inactive';
            case 1:
                return 'Active';
            case 2:
                return 'Archived';
            default:
                return '';
        }
    }
    /**
     * Return selected status or default.
     *
     * @return string
     */
    getSelectedStatusOrDefault() {
        const status = this.getTemplateViewModel().status();
        const statusValue = status === '' || status === null || typeof status === 'undefined' ? ACTIVE_STATUS_VALUE : String(status);
        return isNaN(Number(statusValue)) ? ACTIVE_STATUS_VALUE : statusValue;
    }
}
